/* cspell:disable */
/**
 * Utilities for capturing request bodies on API failures for Datadog.
 *
 * Datadog RUM does NOT capture request bodies for "Resources".
 * These utilities provide redacted + truncated snapshots for debugging.
 */

const shouldCaptureRequestBodyForDatadog = process.env.NEXT_PUBLIC_STYTCH_PROJECT_ENV !== 'test';

const DATADOG_REQUEST_BODY_MAX_CHARS = 4000;

const SENSITIVE_KEYS = new Set([
  // Common auth/security fields
  'password',
  'passcode',
  'otp',
  'token',
  'secret',
  'authorization',
  'cvv',

  // Admin login pass, which logs someone in on its own - snake_case, camelCase, and squished lowercase
  'admin_grant_jwt',
  'adminGrantJwt',
  'admingrantjwt',

  // API keys - snake_case, camelCase, and squished lowercase
  'api_key',
  'apiKey',
  'apikey',

  // Access/refresh tokens - snake_case, camelCase, and squished lowercase
  'access_token',
  'accessToken',
  'accesstoken',
  'refresh_token',
  'refreshToken',
  'refreshtoken',

  // SSN - snake_case, camelCase, and squished lowercase
  'ssn',
  'social_security_number',
  'socialSecurityNumber',
  'socialsecuritynumber',

  // Date of birth - snake_case, camelCase, and squished lowercase
  'dob',
  'date_of_birth',
  'dateOfBirth',
  'dateofbirth',

  // Bank/financial - snake_case, camelCase, and squished lowercase
  'bank_account',
  'bankAccount',
  'bankaccount',
  'account_number',
  'accountNumber',
  'accountnumber',
  'routing_number',
  'routingNumber',
  'routingnumber',
  'card_number',
  'cardNumber',
  'cardnumber',
]);

const redactValue = (value: any, depth = 0): any => {
  if (depth > 8) return '[REDACTED_DEPTH_LIMIT]';
  if (value === null || value === undefined) return value;

  if (Array.isArray(value)) {
    return value.map((v) => redactValue(v, depth + 1));
  }

  if (typeof value === 'object') {
    const out: Record<string, any> = {};
    Object.keys(value).forEach((k) => {
      const keyLower = k.toLowerCase();
      if (SENSITIVE_KEYS.has(keyLower) || SENSITIVE_KEYS.has(k)) {
        out[k] = '[REDACTED]';
        return;
      }
      out[k] = redactValue(value[k], depth + 1);
    });
    return out;
  }

  return value;
};

const safeParseJson = (maybeJson: any): any => {
  if (typeof maybeJson !== 'string') return maybeJson;
  const trimmed = maybeJson.trim();
  if (!trimmed.startsWith('{') && !trimmed.startsWith('[')) return maybeJson;
  try {
    return JSON.parse(trimmed);
  } catch {
    return maybeJson;
  }
};

export interface RequestBodySnapshot {
  body?: string;
  bodyTruncated?: boolean;
}

/**
 * Get a safe, redacted snapshot of a request body for Datadog logging.
 */
export const getRedactedRequestBody = (rawBody: any): RequestBodySnapshot => {
  if (!shouldCaptureRequestBodyForDatadog) return {};
  if (rawBody === undefined || rawBody === null) return {};

  // Handle FormData - extract keys only (values may contain sensitive/binary data)
  try {
    if (typeof FormData !== 'undefined' && rawBody instanceof FormData) {
      // Use type assertion to handle environments where FormData.keys() types are incomplete
      const keys = Array.from((rawBody as globalThis.FormData).keys());
      return { body: JSON.stringify({ formDataKeys: keys }), bodyTruncated: false };
    }
  } catch {
    // ignore instanceof errors in weird environments
  }

  const parsed = safeParseJson(rawBody);
  const redacted = redactValue(parsed);

  try {
    const serialized = typeof redacted === 'string' ? redacted : JSON.stringify(redacted);
    if (serialized.length <= DATADOG_REQUEST_BODY_MAX_CHARS) {
      return { body: serialized, bodyTruncated: false };
    }
    return { body: serialized.slice(0, DATADOG_REQUEST_BODY_MAX_CHARS), bodyTruncated: true };
  } catch {
    return { body: `[UNSERIALIZABLE_${typeof redacted}]`, bodyTruncated: true };
  }
};

export interface RequestContext {
  method: string;
  url: string;
  httpStatus?: number;
  requestBody?: string;
  requestBodyTruncated?: boolean;
}

/**
 * Build request context from an axios error config.
 */
export const buildRequestContext = (error: any): RequestContext | undefined => {
  if (!shouldCaptureRequestBodyForDatadog) return undefined;

  const method = (error?.config?.method || '').toString().toUpperCase();
  const url = (error?.config?.url || '').toString();
  const status = error?.response?.status || error?.status;

  if (!url || !method) return undefined;

  // Skip auth/cancel errors
  if (status === 401 || status === 403 || status === 499) return undefined;

  // Skip GET requests (no meaningful body)
  if (method === 'GET') return undefined;

  const { body, bodyTruncated } = getRedactedRequestBody(error?.config?.data);

  return {
    method,
    url,
    httpStatus: status,
    requestBody: body,
    requestBodyTruncated: bodyTruncated,
  };
};
